import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import lookupTableActions from 'actions/lookupTableActions';
import personActions from 'actions/personActions';

import Grid from 'components/core/Grid';
import Input from 'components/core/Input';
import Button from 'components/core/Button';
import Switch from 'components/core/Switch';
import LookupTableInput from 'components/LookupTableInput';
import ToggleButtonGroup from 'components/core/ToggleButtonGroup';
import Stack from 'components/core/Stack';
import Typography from 'components/core/Typography';
import NoteAdd from 'components/NoteAdd';
import EvidenceAdd from 'components/EvidenceAdd';
import EvidenceAddDialog from 'components/EvidenceAddDialog';

import Observation from 'domain/Observation';

const ObservationForm = ({
    observation: initialObservation,
    errors = {},
    warnings = {},
    notes = [],
    evidence = [],
    onSubmit
}) => {
    const dispatch = useDispatch();
    const options = useSelector(({ lookupTable }) => lookupTable.options);    
    const personList = useSelector(({ person }) => person.list);
    const [observation, setObservation] = useState(initialObservation || new Observation());
    const [dateValue, setDateValue] = useState('');
    const [formNotes, setFormNotes] = useState(notes);
    const [originalNotes, setOriginalNotes] = useState(notes);
    const [formEvidence, setFormEvidence] = useState(evidence || []);
    const [originalEvidence, setOriginalEvidence] = useState(evidence || []);
    const [showEvidenceDialog, setShowEvidenceDialog] = useState(false);

    useEffect(() => {
        dispatch(lookupTableActions.fetchAllOptions());
        dispatch(personActions.list());
    }, [dispatch]);

    useEffect(() => {
        if (initialObservation) {
            setObservation(initialObservation);
            if (initialObservation.get('date')) {
                setDateValue(DateTime.fromJSDate(initialObservation.get('date')).toISODate());
            }
        }
    }, [initialObservation]);

    useEffect(() => {
        setFormNotes(notes);
        setOriginalNotes(notes);
    }, [notes]);

    useEffect(() => {
        setFormEvidence(evidence || []);
        setOriginalEvidence(evidence || []);
    }, [evidence]);

    const handleFieldChange = (field, value) => {
        const updatedObservation = new Observation(observation);
        if (field === 'date') {
            if (value) {
                const date = DateTime.fromISO(value).toJSDate();
                updatedObservation.set('date', date);
                setDateValue(value);
            } else {
                updatedObservation.set('date', null);
                setDateValue('');
            }
        } else if ((field === 'lat' || field === 'lng') && value === '') {
            updatedObservation.set(field, null);
        } else {
            updatedObservation.set(field, value);
        }
        setObservation(updatedObservation);
    };

    const handleChangeLatLong = (field, value) => {
        let cleanedValue = value.replace(/[^0-9.\-]/g, '');
        
        if (cleanedValue.includes('.')) {
            const parts = cleanedValue.split('.');
            cleanedValue = parts[0] + '.' + parts.slice(1).join('');
        }
        
        if (cleanedValue.includes('-')) {
            if (cleanedValue.indexOf('-') === 0) {
                cleanedValue = '-' + cleanedValue.slice(1).replace(/-/g, '');
            } else {
                cleanedValue = cleanedValue.replace(/-/g, '');
            }
        }
        
        handleFieldChange(field, cleanedValue);
    };

    const handleNoteChange = (index, field, value) => {
        const updatedNotes = [...formNotes];
        updatedNotes[index] = { ...updatedNotes[index], [field]: value };
        setFormNotes(updatedNotes);
    };

    const handleAddNote = () => {
        setFormNotes([...formNotes, { type: null, text: '', isDefault: false }]);
    };

    const handleRemoveNote = (index) => {
        const updatedNotes = formNotes.filter((_, i) => i !== index);
        setFormNotes(updatedNotes);
    };

    const getNoteDiff = (original, updated) => {
        const toCreate = updated.filter(note => !note.id && note.type && note.text.trim());
        const toUpdate = updated.filter(note => {
            if (!note.id || !note.text.trim()) return false;
            const originalNote = original.find(orig => orig.id === note.id);
            if (!originalNote) return false;
            return (
                originalNote.type?.id !== note.type?.id || 
                originalNote.text !== note.text
            );
        });
        const toDelete = original.filter(orig => 
            orig.id && !updated.find(upd => upd.id === orig.id)
        );
        return { toCreate, toUpdate, toDelete };
    };

    const getEvidenceDiff = (original, updated) => {
        const toAttach = updated.filter(ev => !original.find(orig => orig.id === ev.id));
        const toDetach = original.filter(orig => !updated.find(upd => upd.id === orig.id));
        return { toAttach, toDetach };
    };

    const handleAddEvidence = (newEvidence) => {
        const isDuplicate = formEvidence.some(ev => ev.id === newEvidence.id);
        if (isDuplicate) {
            return;
        }
        const updatedEvidence = [...formEvidence, newEvidence];
        setFormEvidence(updatedEvidence);
        setShowEvidenceDialog(false);
    };

    const handleRemoveEvidence = (index) => {
        const updatedEvidence = formEvidence.filter((_, i) => i !== index);
        setFormEvidence(updatedEvidence);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (onSubmit) {
            const noteDiff = getNoteDiff(originalNotes, formNotes);
            const evidenceDiff = getEvidenceDiff(originalEvidence, formEvidence);
            onSubmit(observation, formNotes, noteDiff, formEvidence, evidenceDiff);
        }
    };

    const getNameLabel = (name) => {
        const taxonomy = name.get('taxonomy');
        const nameText = name.get('name');
        const primaryName = taxonomy.get('primaryName');
        if (nameText === primaryName) {
            return nameText;
        } else {
            return `${nameText} (${primaryName})`;
        }
    };

    const dateSpecificityOptions = [
        { value: 'yyyy-mm-dd', label: 'Day' },
        { value: 'yyyy-mm', label: 'Month' },
        { value: 'yyyy', label: 'Year' },
    ];

    return (
        <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <LookupTableInput
                        id="name"
                        label="Name"
                        value={observation.name}
                        onChange={(value) => handleFieldChange('name', value)}
                        options={options.names}
                        getOptionLabel={(option) => getNameLabel(option)}
                        error={errors.name}
                        warning={warnings.name}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Stack direction="row" spacing={2}>
                        <ToggleButtonGroup
                            label="Date Specificity"
                            value={observation.get('dateSpecificity')}
                            onChange={(value) => handleFieldChange('dateSpecificity', value)}
                            options={dateSpecificityOptions}
                            disabled={observation.get('event')}
                        />

                        {observation.get('event') ? (
                            <Input
                                label="Date"
                                type="date"
                                format={observation.get('dateSpecificity')}
                                value={observation.get('date') ? DateTime.fromJSDate(observation.get('date')).toISODate() : ''}
                                onChange={(value) => handleFieldChange('date', value)}
                                error={errors.date}
                                warning={warnings.date}
                                disabled
                            />
                        ) : (
                            <Input
                                label="Date"
                                type="date"
                                format={observation.get('dateSpecificity')}
                                value={dateValue}
                                onChange={(value) => handleFieldChange('date', value)}
                                error={errors.date}
                                warning={warnings.date}
                            />
                        )}
                    </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                    <LookupTableInput
                        id="observedBy"
                        label="Observed By"
                        hasSubform
                        value={observation.observedBy}
                        onChange={(value) => handleFieldChange('observedBy', value)}
                        options={personList}
                        getOptionLabel={(option) => {
                            const person = option?.get('person');
                            const personOfficialName = `${person?.get('firstName')} ${person?.get('lastName')}`; 
                            const thisName = `${option?.get('name')}`;
                            if (personOfficialName === thisName) {
                                return personOfficialName;
                            } else {
                                return `${option?.get('name')} (${personOfficialName})`
                            }
                        }}
                        error={errors.observedBy}
                        warning={warnings.observedBy}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <LookupTableInput
                        id="location"
                        label="Location"
                        value={observation.location}
                        onChange={(value) => handleFieldChange('location', value)}
                        options={options.locations}
                        getOptionLabel={(option) => option?.get('label')}
                        error={errors.location}
                        warning={warnings.location}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <LookupTableInput
                        id="town"
                        label="Town"
                        value={observation.town}
                        onChange={(value) => handleFieldChange('town', value)}
                        options={options.towns}
                        getOptionLabel={(option) => option?.get('label')}
                        error={errors.town}
                        warning={warnings.town}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <LookupTableInput
                        id="quality"
                        label="Quality"
                        value={observation.quality}
                        onChange={(value) => handleFieldChange('quality', value)}
                        options={options.qualities}
                        getOptionLabel={(option) => option?.get('label')}
                        error={errors.quality}
                        warning={warnings.quality}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <LookupTableInput
                        id="substantiation"
                        label="Substantiation"
                        value={observation.substantiation}
                        onChange={(value) => handleFieldChange('substantiation', value)}
                        options={options.substantiations}
                        getOptionLabel={(option) => option?.get('label')}
                        error={errors.substantiation}
                        warning={warnings.substantiation}
                    />
                </Grid>

                <Grid item xs={12}  md={6}>
                    <LookupTableInput
                        id="keyword"
                        label="Keyword"
                        value={observation.keyword}
                        onChange={(value) => handleFieldChange('keyword', value)}
                        options={options.keywords}
                        getOptionLabel={(option) => option?.get('label')}
                        error={errors.keyword}
                        warning={warnings.keyword}
                    />
                </Grid>

                <Grid item xs={12}  md={6}>
                    <LookupTableInput
                        id="event"
                        label="Event"
                        value={observation.event}
                        onChange={(value) => handleFieldChange('event', value)}
                        options={options.events}
                        getOptionLabel={(option) => option?.get('name')}
                        error={errors.event}
                        warning={warnings.event}
                    />
                </Grid>

                <Grid item xs={12} md={6}></Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Latitude"
                        type="text"
                        value={observation.get('lat') || ''}
                        onChange={(value) => handleChangeLatLong('lat', value)}
                        error={errors.lat}
                        warning={warnings.lat}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Longitude"
                        type="text"
                        value={observation.get('lng') || ''}
                        onChange={(value) => handleChangeLatLong('lng', value)}
                        error={errors.lng}
                        warning={warnings.lng}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <LookupTableInput
                        id="substrate"
                        label="Substrate"
                        value={observation.substrate}
                        onChange={(value) => handleFieldChange('substrate', value)}
                        options={options.substrates}
                        getOptionLabel={(option) => option?.get('label')}
                        error={errors.substrate}
                        warning={warnings.substrate}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <LookupTableInput
                        id="locDeterminization"
                        label="Location Determined Method"
                        value={observation.locDeterminization}
                        onChange={(value) => handleFieldChange('locDeterminization', value)}
                        options={options.locDeterminization}
                        getOptionLabel={(option) => option?.get('label')}
                        error={errors.locDeterminization}
                        warning={warnings.locDeterminization}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Switch
                        label="Is Detected"
                        checked={observation.get('isDetected')}
                        onChange={(value) => handleFieldChange('isDetected', value)}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Switch
                        label="Observation Restriction"
                        checked={observation.get('observationRestriction')}
                        onChange={(value) => handleFieldChange('observationRestriction', value)}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Switch
                        label="Flag"
                        checked={observation.get('flag')}
                        onChange={(value) => handleFieldChange('flag', value)}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <LookupTableInput
                        id="reviewedBy"
                        label="Reviewed By"
                        hasSubform
                        value={observation.reviewedBy}
                        onChange={(value) => handleFieldChange('reviewedBy', value)}
                        options={personList}
                        getOptionLabel={(option) => {
                            const person = option?.get('person');
                            const personOfficialName = `${person?.get('firstName')} ${person?.get('lastName')}`; 
                            const thisName = `${option?.get('name')}`;
                            if (personOfficialName === thisName) {
                                return personOfficialName;
                            } else {
                                return `${option?.get('name')} (${personOfficialName})`
                            }
                        }}
                        error={errors.reviewedBy}
                        warning={warnings.reviewedBy}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>Notes</Typography>
                </Grid>

                <Grid item xs={12}>
                    <NoteAdd
                        parentForm="Observation"
                        notes={formNotes}
                        options={options}
                        onNoteChange={handleNoteChange}
                        onAddNote={handleAddNote}
                        onRemoveNote={handleRemoveNote}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>Evidence</Typography>
                    <EvidenceAdd
                        evidence={formEvidence}
                        onRemove={handleRemoveEvidence}
                        onOpenDialog={() => setShowEvidenceDialog(true)}
                        observation={observation}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Button type="submit">
                        {initialObservation?.id ? 'Update' : 'Create'} Observation
                    </Button>
                </Grid>
            </Grid>
            
            {showEvidenceDialog && (
                <EvidenceAddDialog
                    open={showEvidenceDialog}
                    onClose={() => setShowEvidenceDialog(false)}
                    mode="form"
                    onSelect={handleAddEvidence}
                    excludeEvidence={formEvidence}
                />
            )}
        </form>
    );
};

export default ObservationForm;